import { createContext, useContext, useEffect, useReducer } from 'react';

export type BoxAction =
  | { type?: never }
  | {
      type:
        | 'clearVisibleItems'
        | 'deselectAll'
        | 'reset'
        | 'resetSelection'
        | 'selectAll';
    }
  | { type: 'configure'; payload: { active: boolean; identifier: string } }
  | {
      itemId: number | string;
      itemValue: boolean;
      type: 'changeSelection';
    }
  | {
      itemId: number | string;
      type: 'setVisibleItem';
    };

export type BoxState = {
  checkboxIdentifier: string;
  checkboxIsActive: boolean;
  selectedItems: { [selected: string]: true };
  visibleItems: (string | number)[];
};

const reducer = (state: BoxState, action: BoxAction): BoxState => {
  if (!action.type) {
    return state;
  } else if (action.type === 'changeSelection') {
    const { itemId, itemValue } = action;
    const { selectedItems } = state;

    if (itemValue) selectedItems[itemId] = true;
    else delete selectedItems[itemId];

    return { ...state, selectedItems: { ...selectedItems } };
  } else if (action.type === 'clearVisibleItems') {
    return { ...state, visibleItems: [] };
  } else if (action.type === 'configure') {
    return {
      ...state,
      checkboxIsActive: action.payload.active,
      checkboxIdentifier: action.payload.identifier
    };
  } else if (action.type === 'deselectAll') {
    const { selectedItems, visibleItems } = state;
    visibleItems.forEach((item) => {
      delete selectedItems[item];
    });

    return { ...state, selectedItems: { ...selectedItems } };
  } else if (action.type === 'reset') {
    return { ...initialState, selectedItems: {}, visibleItems: [] };
  } else if (action.type === 'resetSelection') {
    return { ...state, selectedItems: {} };
  } else if (action.type === 'selectAll') {
    return {
      ...state,
      selectedItems: {
        ...state.selectedItems,
        ...state.visibleItems.reduce((allItems, item) => {
          allItems[item] = true;
          return allItems;
        }, {})
      }
    };
  } else if (action.type === 'setVisibleItem') {
    return { ...state, visibleItems: [...state.visibleItems, action.itemId] };
  }

  return state;
};

const initialState: BoxState = {
  checkboxIdentifier: '',
  checkboxIsActive: false,
  selectedItems: {},
  visibleItems: []
};

const BoxStateContext = createContext<BoxState>(initialState);

const BoxDispatchContext = createContext<React.Dispatch<BoxAction>>(() => {});

interface Props {
  children: React.ReactNode;
  getDispatch?: (dispatch: React.Dispatch<BoxAction>) => void;
}

const BoxProvider = ({ children, getDispatch }: Props) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    // when the provider mounts, reset the internal checkbox state
    dispatch({ type: 'reset' });
  }, []);

  useEffect(() => {
    if (getDispatch) getDispatch(dispatch);
  }, [getDispatch]);

  return (
    <BoxStateContext.Provider value={state}>
      <BoxDispatchContext.Provider value={dispatch}>
        {children}
      </BoxDispatchContext.Provider>
    </BoxStateContext.Provider>
  );
};

const useBoxContext = () => {
  const state = useContext(BoxStateContext);
  return state;
};

const useBoxDispatch = () => {
  const dispatch = useContext(BoxDispatchContext);
  return dispatch;
};

export { BoxProvider, useBoxContext, useBoxDispatch };
